import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { VACIO, ETIQUETA_VACIO } from './filtros';
import Popover from './Popover';

const etiquetaValor = (v) => (v === VACIO ? ETIQUETA_VACIO : v);

const resumen = (actuales) => {
  if (!actuales.length) return 'Todos';
  if (actuales.length === 1) return etiquetaValor(actuales[0]);
  return `${actuales.length} seleccionados`;
};

// Desplegable de selección múltiple (casillas) con el nº de registros de cada valor.
const CampoMulti = ({ col, actuales, opciones, onChange }) => {
  const [ancla, setAncla] = useState(null);
  const conocidos = new Set(opciones.map((o) => o.valor));
  const lista = [
    ...actuales.filter((v) => !conocidos.has(v)).map((valor) => ({ valor, n: 0 })),
    ...opciones,
  ];
  const alternar = (valor) =>
    onChange(actuales.includes(valor) ? actuales.filter((v) => v !== valor) : [...actuales, valor]);

  return (
    <div className="tbl-panel-campo">
      <span className="tbl-panel-etq">{col.label}</span>
      <button
        type="button"
        className="tbl-panel-multi"
        aria-label={col.label}
        aria-haspopup="dialog"
        aria-expanded={!!ancla}
        onClick={(e) => setAncla(ancla ? null : e.currentTarget)}
      >
        <span>{resumen(actuales)}</span>
      </button>
      <Popover ancla={ancla} onClose={() => setAncla(null)} ancho={260} etiqueta={col.label}>
        <div className="tbl-panel-lista">
          <label className="tbl-panel-op">
            <input type="checkbox" checked={!actuales.length} onChange={() => onChange([])} />
            Todos
          </label>
          {lista.map((o) => (
            <label key={o.valor} className="tbl-panel-op">
              <input
                type="checkbox"
                checked={actuales.includes(o.valor)}
                onChange={() => alternar(o.valor)}
              />
              {etiquetaValor(o.valor)} ({o.n})
            </label>
          ))}
        </div>
      </Popover>
    </div>
  );
};

CampoMulti.propTypes = {
  col: PropTypes.object.isRequired,
  actuales: PropTypes.array.isRequired,
  opciones: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SIN_FACETAS = () => Promise.resolve([]);

/**
 * Panel de filtros rápidos bajo la caja de búsqueda: un desplegable por columna con sus valores
 * reales (con el nº de registros). Escribe en los mismos filtros por columna de la tabla, así que
 * se guardan por usuario y aparecen como chips en «Filtros activos».
 */
const PanelFiltros = ({ tabla, ids, loadFacets = SIN_FACETAS }) => {
  const [facetas, setFacetas] = useState({});
  const clave = JSON.stringify(tabla.filtros);

  useEffect(() => {
    let vivo = true;
    ids.forEach((id) => {
      Promise.resolve(loadFacets(id))
        .then((v) => vivo && setFacetas((prev) => ({ ...prev, [id]: v || [] })))
        .catch(() => {});
    });
    return () => {
      vivo = false;
    };
    // Las facetas cambian al cambiar los demás filtros.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, ids.join(','), loadFacets]);

  const columnas = ids.map((id) => tabla.columnas.find((c) => c.id === id)).filter(Boolean);

  const elegir = (id, valores) => tabla.fijarFiltro(id, valores.length ? { valores } : null);

  return (
    <div className="tbl-panel-filtros" role="group" aria-label="Filtros">
      {columnas.map((col) => (
        <CampoMulti
          key={col.id}
          col={col}
          actuales={tabla.filtros[col.id]?.valores || []}
          opciones={facetas[col.id] || []}
          onChange={(v) => elegir(col.id, v)}
        />
      ))}
    </div>
  );
};

PanelFiltros.propTypes = {
  tabla: PropTypes.object.isRequired,
  ids: PropTypes.array.isRequired,
  loadFacets: PropTypes.func,
};

export default PanelFiltros;
